import React, { Suspense, lazy } from 'react'
import { Divider, Separator, Skeleton, colors } from '@fs/zion-ui'
import Head from '@fs/zion-head'
import ErrorBoundary from '@fs/zion-error-boundary'
import { Global, css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { useLocation } from '@fs/zion-router'
import favicon from './assets/favicon.ico'
import { useIsMdWidth } from './hooks'
import { ErrorFallback, FeedbackButton, BreadcrumbListStructuredData, WebSiteStructuredData } from './lib'
import { useFeedbackContext, useLoggedOutOverlay } from './providers'

const NavBar = lazy(() => import('./components/header/NavBar'))
const AppRoutes = lazy(() => import('./routes/App.route'))
const AppProviders = lazy(() => import('./AppProviders'))
const QAInfoSheet = lazy(() => import('./lib/qa/QAInfoSheet'))
const LoggedOutOverlay = lazy(() => import('./lib/overlays/LoggedOutOverlay'))
const Footer = lazy(() => import('./components/footer/Footer'))
const PageTitle = lazy(() => import('./components/header/PageTitle'))
const FloatingChatButton = lazy(() => import('./lib/buttons/FloatingChatButton/FloatingChatButton'))

const headingOverrideCss = css`
  body {
    --font-families-headings: var(--font-families-body);
  }
`
const InitialSkeleton = (): JSX.Element => {
  const isMdWidth = useIsMdWidth()
  return (
    <div style={{ background: colors.gray00 }}>
      {/* Nav Bar */}
      <Skeleton.Image width="100%" height={72} />
      <Divider />
      {/* Header */}
      <Skeleton.Image height={isMdWidth ? 400 : 680} width="100%" />
    </div>
  )
}

const Favicon = (): JSX.Element => {
  return (
    <Head>
      <link rel="icon" href={favicon} />
    </Head>
  )
}

const App = (): JSX.Element => {
  const qaOptionsFlag = useFeatureFlag('rootstech_qa_options')
  const { shouldHideGlobalFeedback } = useFeedbackContext()
  const { overlay } = useLoggedOutOverlay()

  const { pathname } = useLocation()
  const backPaths = ['/playlist', '/registered-session/']
  const guestRedirect = backPaths.includes(pathname) ? '/back' : ''

  return (
    <Suspense fallback={<InitialSkeleton />}>
      <NavBar />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <main id="starting-content">
          <AppRoutes />
        </main>
      </ErrorBoundary>
      <Separator size="xxl" />
      <FloatingChatButton />
      {qaOptionsFlag.isOn && <QAInfoSheet />}
      {!shouldHideGlobalFeedback && <FeedbackButton />}
      <Footer />

      {(overlay.isOpen || overlay.transitioning) && <LoggedOutOverlay {...overlay} guestRedirect={guestRedirect} />}
    </Suspense>
  )
}

export default function AppContainer(): JSX.Element {
  return (
    <>
      <PageTitle />
      <Favicon />
      <WebSiteStructuredData />
      <BreadcrumbListStructuredData />
      <Global styles={headingOverrideCss} />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <AppProviders>
          <App />
        </AppProviders>
      </ErrorBoundary>
    </>
  )
}
